import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getEffectiveApiUrl } from '../config/runtimeEnv';
import { signalRService } from '../services/signalRService';
import { metricsSignalRService } from '../services/metricsSignalRService';
import { useAuthStore } from '../store/authStore';
import { useContextStore } from '../store/contextStore';

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

interface SignalRContextValue {
  hubBaseUrl: string;
  agentExecutionStatus: ConnectionStatus;
  metricsStatus: ConnectionStatus;
}

const SignalRContext = createContext<SignalRContextValue | null>(null);

/**
 * Provides app-level SignalR connection lifecycle.
 *
 * - Ensures we maintain a single connection per hub for the whole app
 * - Avoids connect/disconnect churn across components
 */
export const SignalRProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const token = useAuthStore((s) => s.token);
  const currentTenantId = useContextStore((s) => s.currentTenant?.id ?? null);
  const currentProjectId = useContextStore((s) => s.currentProject?.id ?? null);

  const hubBaseUrl = useMemo(() => {
    const apiUrl = getEffectiveApiUrl();
    const base = apiUrl.replace(/\/api\/?$/, '');
    return base || (typeof window !== 'undefined' ? window.location.origin : '');
  }, []);

  const [agentExecutionStatus, setAgentExecutionStatus] = useState<ConnectionStatus>('disconnected');
  const [metricsStatus, setMetricsStatus] = useState<ConnectionStatus>('disconnected');

  // Agent execution hub: connect once per authenticated session.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!token) {
        setAgentExecutionStatus('disconnected');
        await signalRService.disconnect();
        return;
      }

      try {
        setAgentExecutionStatus('connecting');
        await signalRService.connect(hubBaseUrl, token);
        if (!cancelled) setAgentExecutionStatus('connected');
      } catch {
        if (!cancelled) setAgentExecutionStatus('disconnected');
      }
    };

    // Track automatic-reconnect state transitions so components that
    // depend on agentExecutionStatus (e.g. MessageListWithStreaming)
    // re-run their effects and re-join session groups.
    signalRService.setReconnectionCallbacks(
      () => { if (!cancelled) setAgentExecutionStatus('connecting'); },
      () => { if (!cancelled) setAgentExecutionStatus('connected'); },
    );

    run();

    return () => {
      cancelled = true;
      signalRService.setReconnectionCallbacks(null, null);
    };
  }, [hubBaseUrl, token]);

  // Metrics hub: connect when we have tenant context.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!token || !currentTenantId) {
        setMetricsStatus('disconnected');
        await metricsSignalRService.disconnect();
        return;
      }

      try {
        setMetricsStatus('connecting');
        await metricsSignalRService.connect(hubBaseUrl, token, currentTenantId, currentProjectId ?? undefined);
        if (!cancelled) setMetricsStatus('connected');
      } catch {
        if (!cancelled) setMetricsStatus('disconnected');
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [currentProjectId, currentTenantId, hubBaseUrl, token]);

  return (
    <SignalRContext.Provider value={{ hubBaseUrl, agentExecutionStatus, metricsStatus }}>
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalR = (): SignalRContextValue => {
  const ctx = useContext(SignalRContext);
  if (!ctx) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return ctx;
};

